import { motion, AnimatePresence } from 'framer-motion'
import { X, Filter, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFilterDefinitions, filterTypeLabels } from '@/hooks/useFilters'
import { useFilterStore } from '@/stores/filterStore'
import type { FilterType } from '@/types'

interface FilterSidebarProps {
  isMobileOpen?: boolean
  onMobileClose?: () => void
  availableFilterValues?: Record<FilterType, string[]>
}

export function FilterSidebar({ isMobileOpen, onMobileClose, availableFilterValues }: FilterSidebarProps) {
  const { data: filterDefinitions, isLoading } = useFilterDefinitions()
  const { filters, toggleFilter, clearFilters, hasActiveFilters } = useFilterStore()

  const filterTypes: FilterType[] = ['product', 'water_tightness', 'fire_class', 'material']

  const sidebarContent = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-secondary-600" />
          <h3 className="font-semibold text-secondary-900">Filters</h3>
        </div>
        {hasActiveFilters() && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1.5 text-sm text-secondary-500 hover:text-primary-600 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-5 bg-secondary-200 rounded w-24 mb-3" />
              <div className="space-y-2">
                <div className="h-4 bg-secondary-200 rounded w-full" />
                <div className="h-4 bg-secondary-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        filterTypes.map((type) => {
          const definitions = filterDefinitions?.[type] || []
          // Filter definitions to only show ones that have products
          const availableDefinitions = availableFilterValues
            ? definitions.filter(def => availableFilterValues[type]?.includes(def.value))
            : definitions

          if (availableDefinitions.length === 0) return null

          return (
            <div key={type}>
              <h4 className="text-sm font-medium text-secondary-700 mb-3">
                {filterTypeLabels[type]}
              </h4>
              <div className="space-y-2">
                {availableDefinitions.map((def) => {
                  const isSelected = filters[type].includes(def.value)
                  return (
                    <label
                      key={def.id}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleFilter(type, def.value)}
                        className="sr-only"
                      />
                      <span
                        className={cn(
                          'w-5 h-5 rounded border-2 flex items-center justify-center transition-all',
                          isSelected
                            ? 'bg-primary-600 border-primary-600'
                            : 'border-secondary-300 group-hover:border-primary-400'
                        )}
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </span>
                      <span
                        className={cn(
                          'text-sm transition-colors',
                          isSelected ? 'text-secondary-900 font-medium' : 'text-secondary-600'
                        )}
                      >
                        {def.label}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })
      )}
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-24 card p-5">{sidebarContent}</div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onMobileClose}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 w-80 bg-white z-50 lg:hidden overflow-y-auto"
            >
              <div className="p-5">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-secondary-900">Filters</h2>
                  <button
                    onClick={onMobileClose}
                    className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {sidebarContent}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
